import React, { useEffect, useState } from "react";
import { Card, Select, Spin, Alert, Button, Space } from "antd";
import { NumberInput } from "../components/number_input";
import {
  useGetPayrollSettingsMap,
  useUpsertPayrollSetting,
} from "~/services/payroll";

export const PayrollTab: React.FC = () => {
  const { data: settingsMap, isLoading, error } = useGetPayrollSettingsMap();
  const upsertSetting = useUpsertPayrollSetting();

  // Local state for form values
  const [formValues, setFormValues] = useState({
    pay_period: "weekly",
    payment_day: "Friday",
    week_start_day: "Sunday",
    week_end_day: "Saturday",
    overtime_threshold: "40",
    overtime_multiplier: "1.5",
  });

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Update form values when settings are loaded
  useEffect(() => {
    if (settingsMap) {
      const newValues = {
        pay_period: settingsMap.pay_period || "weekly",
        payment_day: settingsMap.payment_day || "Friday",
        week_start_day: settingsMap.week_start_day || "Sunday",
        week_end_day: settingsMap.week_end_day || "Saturday",
        overtime_threshold: settingsMap.overtime_threshold || "40",
        overtime_multiplier: settingsMap.overtime_multiplier || "1.5",
      };
      setFormValues(newValues);
      setHasChanges(false);
    }
  }, [settingsMap]);

  const handleSettingChange = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      // Save all settings
      const settingPromises = Object.entries(formValues).map(([key, value]) =>
        upsertSetting.mutateAsync({
          key,
          dto: { value },
        })
      );

      await Promise.all(settingPromises);
      setHasChanges(false);
    } catch (error) {
      console.log(error);
      // Error handling is done in the mutation hook
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading payroll settings"
        description="Failed to load payroll configuration. Please try again."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Payroll & Employees</h3>
        <p className="text-gray-500 mb-4">
          Configure payroll settings and view payroll information
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card title="Pay Period Settings" className="w-full">
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pay Period
              </label>
              <Select
                value={formValues.pay_period}
                className="w-full"
                onChange={(value) => handleSettingChange("pay_period", value)}
              >
                <Select.Option value="weekly">Weekly</Select.Option>
                <Select.Option value="biweekly">Bi-Weekly</Select.Option>
                <Select.Option value="monthly">Monthly</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Day
              </label>
              <Select
                value={formValues.payment_day}
                className="w-full"
                onChange={(value) => handleSettingChange("payment_day", value)}
              >
                <Select.Option value="Friday">Friday</Select.Option>
                <Select.Option value="Thursday">Thursday</Select.Option>
                <Select.Option value="Wednesday">Wednesday</Select.Option>
                <Select.Option value="Tuesday">Tuesday</Select.Option>
                <Select.Option value="Monday">Monday</Select.Option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Week Start Day
              </label>
              <Select
                value={formValues.week_start_day}
                className="w-full"
                onChange={(value) =>
                  handleSettingChange("week_start_day", value)
                }
              >
                <Select.Option value="Sunday">Sunday</Select.Option>
                <Select.Option value="Monday">Monday</Select.Option>
                <Select.Option value="Saturday">Saturday</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Week End Day
              </label>
              <Select
                value={formValues.week_end_day}
                className="w-full"
                onChange={(value) => handleSettingChange("week_end_day", value)}
              >
                <Select.Option value="Saturday">Saturday</Select.Option>
                <Select.Option value="Sunday">Sunday</Select.Option>
                <Select.Option value="Friday">Friday</Select.Option>
              </Select>
            </div>
          </div>
        </Card>

        <Card title="Overtime Settings" className="w-full">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overtime Threshold (hours)
              </label>
              <NumberInput
                value={parseFloat(formValues.overtime_threshold)}
                suffix="hours"
                onChange={(value) =>
                  handleSettingChange(
                    "overtime_threshold",
                    value?.toString() || "40"
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overtime Multiplier
              </label>
              <NumberInput
                value={parseFloat(formValues.overtime_multiplier)}
                suffix="x"
                onChange={(value) =>
                  handleSettingChange(
                    "overtime_multiplier",
                    value?.toString() || "1.5"
                  )
                }
                step={0.1}
                min={1}
                max={5}
              />
            </div>
          </div>
        </Card>

        {/* Save Settings Button */}
        <Card className="w-full">
          <div className="flex justify-end">
            <Space>
              <Button
                type="primary"
                onClick={handleSaveSettings}
                loading={upsertSetting.isPending}
                disabled={!hasChanges}
              >
                Save Settings
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    </div>
  );
};
